import axiosClient from "utils/axios";
import { ApiList, ApiResponse, ApiOptionList } from "../../../types/api.types";
import type {
  CreateMaterialRequestPayload,
  MaterialRequest,
  MaterialRequestListFilters,
  MaterialRequestOption,
  RejectMaterialRequestPayload,
  UpdateMaterialRequestPayload,
} from "features/MaterialRequests/types/material-request.types";

export const getMaterialRequests = async (
  params: MaterialRequestListFilters = {},
) => {
  try {
    const {
      page = 1,
      limit = 10,
      search,
      status,
      sourceType,
      sourceId,
    } = params;
    const sid =
      sourceId != null && sourceId !== ("" as unknown as number)
        ? Number(sourceId)
        : NaN;
    const response = await axiosClient.get<
      ApiResponse<ApiList<MaterialRequest[]>>
    >("/material-requests", {
      params: {
        page,
        limit,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(sourceType ? { sourceType } : {}),
        ...(Number.isFinite(sid) && sid > 0 ? { sourceId: sid } : {}),
      },
    });
    return response.data;
  } catch (err) {
    console.warn("Error fetching Material Request  : ", err);
    throw err;
  }
};

export const createMaterialRequest = async (
  payload: CreateMaterialRequestPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialRequest>>(
      "/material-requests",
      {
        ...payload,
        items: payload.items ?? [],
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Material Request : ", err);
    throw err;
  }
};

export type UpsertDraftMaterialRequestResult = {
  body: ApiResponse<MaterialRequest>;
  httpStatus: number;
};

export const upsertDraftMaterialRequest = async (
  payload: CreateMaterialRequestPayload,
): Promise<UpsertDraftMaterialRequestResult> => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialRequest>>(
      "/material-requests/upsert-draft",
      {
        ...payload,
        items: payload.items ?? [],
      },
    );
    return { body: response.data, httpStatus: response.status };
  } catch (err) {
    console.warn("Error upserting draft Material Request : ", err);
    throw err;
  }
};

export const submitMaterialRequest = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialRequest>>(
      `/material-requests/${id}/submit`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error submitting Material Request : ", err);
    throw err;
  }
};

export const getMaterialRequestOptions = async ({
  query,
  page = 1,
  limit = 50,
  status,
  sourceType,
  sourceId,
  ids,
  withoutActivePo,
}: {
  query?: string;
  page?: number;
  limit?: number;
  status?: string;
  sourceType?: string;
  sourceId?: number;
  ids?: number[];
  withoutActivePo?: boolean;
} = {}) => {
  try {
    const lim = Math.min(Math.max(1, limit), 100);
    const response = await axiosClient.get<
      ApiResponse<{ rows: MaterialRequestOption[]; hasMore: boolean }>
    >("/material-requests/options", {
      params: {
        query: query?.trim() || undefined,
        page,
        limit: lim,
        ...(status ? { status } : {}),
        ...(sourceType ? { sourceType } : {}),
        ...(sourceId != null && sourceId > 0 ? { sourceId } : {}),
        ...(ids?.length ? { ids: ids.join(",") } : {}),
        ...(withoutActivePo ? { withoutActivePo: true } : {}),
      },
    });
    return response.data as ApiResponse<ApiOptionList>;
  } catch (err) {
    console.warn("Error fetching material request options: ", err);
    throw err;
  }
};

export const getMaterialRequest = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<MaterialRequest>>(
      `/material-requests/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Material Request : ", err);
    throw err;
  }
};

export const updateMaterialRequest = async (
  id: number,
  payload: UpdateMaterialRequestPayload,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<MaterialRequest>>(
      `/material-requests/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Material Request : ", err);
    throw err;
  }
};

export const approveMaterialRequest = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialRequest>>(
      `/material-requests/${id}/approve`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error approving Material Request : ", err);
    throw err;
  }
};

export const rejectMaterialRequest = async (
  id: number,
  payload?: RejectMaterialRequestPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialRequest>>(
      `/material-requests/${id}/reject`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error rejecting Material Request : ", err);
    throw err;
  }
};

export const cancelMaterialRequest = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<MaterialRequest>>(
      `/material-requests/${id}/cancel`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling Material Request : ", err);
    throw err;
  }
};

export const deleteMaterialRequest = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<null>>(
      `/material-requests/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Material Request : ", err);
    throw err;
  }
};
